"use client";

import { Button, cn, Input } from "@devhop/ui";
import { CalendarRange, Filter, Search, User, X } from "lucide-react";
import { useState } from "react";
import { DatePickerField } from "@/modules/merchant/presentation/ui/DatePickerField";
import { POS_SALE_STATUS_OPTIONS } from "@/modules/merchant/presentation/ui/shop-pos/pos-sale-status";

export type ShopPosSalesHistoryFiltersProps = {
  disabled?: boolean;
  statusKey: string;
  createdFrom?: string;
  createdTo?: string;
  saleNumberQ?: string;
  customerSearchQ?: string;
  activeFilterCount: number;
  onStatusChange: (value: string) => void;
  onCreatedFromChange: (value: string | undefined) => void;
  onCreatedToChange: (value: string | undefined) => void;
  onSaleNumberChange: (value: string | undefined) => void;
  onCustomerSearchChange: (value: string | undefined) => void;
  onClear: () => void;
};

export function ShopPosSalesHistoryFilters({
  disabled,
  statusKey,
  createdFrom,
  createdTo,
  saleNumberQ,
  customerSearchQ,
  activeFilterCount,
  onStatusChange,
  onCreatedFromChange,
  onCreatedToChange,
  onSaleNumberChange,
  onCustomerSearchChange,
  onClear,
}: ShopPosSalesHistoryFiltersProps) {
  const [expanded, setExpanded] = useState(activeFilterCount > 0);

  return (
    <section className="mb-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {/* Status pills — always visible */}
      <div className="border-border border-b bg-muted/10 px-4 py-3 sm:px-5">
        <p className="mb-2 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
          ສະຖານະ
        </p>
        <div className="-mx-1 flex flex-wrap gap-1.5 px-1 pb-0.5">
          {POS_SALE_STATUS_OPTIONS.map((opt) => {
            const active = statusKey === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => onStatusChange(opt.value)}
                className={cn(
                  "inline-flex h-8 shrink-0 items-center rounded-lg border px-3 font-medium text-xs transition-colors touch-manipulation",
                  "disabled:pointer-events-none disabled:opacity-50",
                  active
                    ? "border-primary/40 bg-primary/15 text-primary"
                    : "border-border bg-background text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Advanced filters toggle (mobile) */}
      <div className="flex items-center justify-between gap-2 border-border border-b px-4 py-2 sm:hidden">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 font-medium text-foreground text-sm"
          onClick={() => setExpanded((v) => !v)}
        >
          <Filter className="size-4 text-[hsl(210_90%_48%)]" aria-hidden />
          ຕົວກອງເພີ່ມເຕີມ
          {activeFilterCount > 0 ? (
            <span className="rounded-full bg-primary/15 px-1.5 py-0.5 font-semibold text-[10px] text-primary tabular-nums">
              {activeFilterCount}
            </span>
          ) : null}
        </button>
        {activeFilterCount > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-muted-foreground"
            disabled={disabled}
            onClick={onClear}
          >
            <X className="size-3.5" aria-hidden />
            ລ້າງ
          </Button>
        ) : null}
      </div>

      {/* Filter fields */}
      <div
        className={cn(
          "grid gap-3 p-4 sm:grid-cols-2 sm:px-5 lg:grid-cols-5",
          !expanded && "hidden sm:grid",
        )}
      >
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 font-medium text-card-foreground text-xs">
            <CalendarRange
              className="size-3.5 text-[hsl(210_90%_48%)]"
              aria-hidden
            />
            ວັນທີເລີ່ມ
          </p>
          <DatePickerField
            value={createdFrom}
            disabled={disabled}
            placeholder="ເລືອກວັນທີເລີ່ມ"
            className="sm:max-w-52"
            onChange={(v) => onCreatedFromChange(v || undefined)}
          />
        </div>
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 font-medium text-card-foreground text-xs">
            <CalendarRange
              className="size-3.5 text-[hsl(210_90%_48%)]"
              aria-hidden
            />
            ວັນທີສິ້ນ
          </p>
          <DatePickerField
            value={createdTo}
            disabled={disabled}
            placeholder="ເລືອກວັນທີສິ້ນ"
            className="sm:max-w-52"
            onChange={(v) => onCreatedToChange(v || undefined)}
          />
        </div>
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 font-medium text-card-foreground text-xs">
            <Search className="size-3.5 text-[hsl(210_90%_48%)]" aria-hidden />
            ເລກບິນ
          </p>
          <Input
            className="h-9 font-mono"
            placeholder="ຄົ້ນບາງສ່ວນ"
            defaultValue={saleNumberQ ?? ""}
            key={`saleNum-${saleNumberQ ?? ""}`}
            disabled={disabled}
            onBlur={(e) => {
              const v = e.target.value.trim() || undefined;
              if (v === saleNumberQ) return;
              onSaleNumberChange(v);
            }}
          />
        </div>
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 font-medium text-card-foreground text-xs">
            <User className="size-3.5 text-[hsl(210_90%_48%)]" aria-hidden />
            ລູກຄ້າ
          </p>
          <Input
            className="h-9"
            placeholder="ຊື່ / ອີເມວ"
            defaultValue={customerSearchQ ?? ""}
            key={`cust-${customerSearchQ ?? ""}`}
            disabled={disabled}
            onBlur={(e) => {
              const v = e.target.value.trim() || undefined;
              if (v === customerSearchQ) return;
              onCustomerSearchChange(v);
            }}
          />
        </div>

        <div className="hidden items-end sm:flex">
          {activeFilterCount > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 gap-1.5"
              disabled={disabled}
              onClick={onClear}
            >
              <X className="size-3.5" aria-hidden />
              ລ້າງຕົວກອງ
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
